/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { AlertRequest, OriginType, SanctioningBodyEnum } from 'models/viewModels';
import { AlertDataService, StreamRequestType } from 'services/stream/RSocket';

export class AlertChannelSettingsService {
  private settingsIdToAlertServiceMap = new Map<string, Map<SanctioningBodyEnum, AlertDataService>>();

  // To initialize Alert RSocket services for the given settingsId
  private setAlertServicesBySettingsId(settingsId: string) {
    this.settingsIdToAlertServiceMap.set(
      settingsId,
      new Map(
        (Object.values(SanctioningBodyEnum) as SanctioningBodyEnum[])
          .filter((x) => typeof x === 'number')
          .map((sanctioningBody) => [
            sanctioningBody,
            new AlertDataService({
              route: 'channel.alert',
              connectionType: StreamRequestType.MotorsportsStreamApi,
              sanctioningBody: sanctioningBody,
              // supported origin type is UNKNOWN from Stream API
              originType: OriginType.UNKNOWN,
            }),
          ])
      )
    );
  }

  // Gets the AlertDataService for the given settingsId and sanctioningBody
  public getAlertDataServiceBySettingsIdSanctioningBody(settingsId: string, sanctioningBody: SanctioningBodyEnum) {
    return this.settingsIdToAlertServiceMap.get(settingsId)?.get(sanctioningBody);
  }

  // Initializes the channel for the given settingsId with the initial filter
  public initializeChannelBySettingsId = (
    settingsId: string,
    sanctioningBody: SanctioningBodyEnum,
    initialFilter: AlertRequest
  ) => {
    this.setAlertServicesBySettingsId(settingsId);
    this.getAlertDataServiceBySettingsIdSanctioningBody(settingsId, sanctioningBody)?.setCurrentFilter(initialFilter);
  };

  // If the service is initailized for the given settingsId  & sanctioningBody, then pass the next request
  public updateRequestBySettingsId = (
    settingsId: string,
    sanctioningBody: SanctioningBodyEnum,
    filter: AlertRequest
  ) => {
    const alertService = this.settingsIdToAlertServiceMap.get(settingsId)?.get(sanctioningBody);
    if (alertService) {
      alertService.updateRequest(filter);
    }
  };

  // Checks if the channel is currently using the given filter
  public isFilterActiveBySettingsId = (
    settingsId: string,
    sanctioningBody: SanctioningBodyEnum,
    newFilter: AlertRequest
  ): boolean => {
    return (
      this.getAlertDataServiceBySettingsIdSanctioningBody(settingsId, sanctioningBody)?.isStreamActiveByFilter(
        newFilter
      ) ?? false
    );
  };

  public removeListenerById = (settingsId: string, sanctioningBody: SanctioningBodyEnum, listenerId: string) => {
    this.settingsIdToAlertServiceMap.get(settingsId)?.get(sanctioningBody)?.removeListener(listenerId);
  };

  public removeAllListeners = (settingsId: string) => {
    this.settingsIdToAlertServiceMap.get(settingsId)?.forEach((service) => {
      service.removeAllListeners();
    });
    // Delete the service so that it can be re-initialized
    this.settingsIdToAlertServiceMap.delete(settingsId);
  };
}

export const alertChannelSettingsService = new AlertChannelSettingsService();
